import {
  FeeTableColumnType,
  FeeTableRowType,
  PageSectionType,
  RecommendationType,
  SourceLinkType,
  SwiftCountryFeeType,
  WithdrawalMethodSummaryType,
} from './types';

export const PAGE_SECTIONS: PageSectionType[] = [
  { id: 'summary', title: 'Summary' },
  { id: 'wise', title: 'Wise' },
  { id: 'bank-transfer', title: 'Bank transfer' },
  { id: 'paypal', title: 'PayPal' },
  { id: 'conversion-fees', title: 'Conversion fees' },
  { id: 'receiving-fees', title: 'Receiving fees' },
  { id: 'recommendations', title: 'Recommendations' },
];

export const METHOD_SUMMARIES: WithdrawalMethodSummaryType[] = [
  {
    id: 'wise',
    title: 'Wise',
    badge: 'Cheapest',
    headlineFee: 'Free in USD',
    description:
      'Payout straight to your Wise account. Free and usually instant in USD; only a conversion fee (0.22%-4.42%) if you receive another currency.',
    bestFor: 'Publishers with a Wise account',
  },
  {
    id: 'bank-transfer',
    title: 'Bank transfer',
    badge: 'Most flexible',
    headlineFee: 'From 1.13 USD',
    description:
      '1.13 USD flat to a US bank account, ~0.4%-2% conversion fee on local rails for most countries, or a fixed 7.41-35 USD via SWIFT.',
    bestFor: 'Most countries and currencies',
  },
  {
    id: 'paypal',
    title: 'PayPal',
    badge: 'Simplest',
    headlineFee: '2% flat',
    description:
      'Payout to your PayPal account. A flat 2% fee on any amount, in any country. PayPal applies its own exchange rate for non-USD accounts.',
    bestFor: 'Publishers without a bank or Wise account',
  },
];

export const SUMMARY_TABLE_COLUMNS: FeeTableColumnType[] = [
  { title: 'Method' },
  { title: 'How it works' },
  { title: 'Typical fee', isFee: true },
  { title: 'Best for' },
];

export const SUMMARY_TABLE_ROWS: FeeTableRowType[] = [
  {
    cells: [
      'Wise (direct)',
      'Payout straight to your Wise account',
      'Free in USD; 0.22%-4.42% conversion fee for other currencies',
      'Cheapest and fastest option',
    ],
  },
  {
    cells: [
      'Bank transfer, local rails',
      'Wise converts USD to your local currency and pays out through the local banking network (ACH, SEPA, Faster Payments, etc.)',
      '~0.4%-2% conversion fee + small fixed fee',
      'Most countries and currencies',
    ],
  },
  {
    cells: [
      'Bank transfer, USD domestic (ACH)',
      'USD payout to a US bank account',
      '1.13 USD fixed',
      'US-based publishers',
    ],
  },
  {
    cells: [
      'Bank transfer, SWIFT',
      'USD (or EUR/GBP) stays in that currency and is sent to a bank account abroad via the SWIFT network',
      'Fixed fee 7.41-35.00 USD, depends on country',
      'Publishers who must receive USD/EUR/GBP outside the US/SEPA/UK',
    ],
  },
  {
    cells: [
      'PayPal',
      'Payout to your PayPal account',
      '2% of the withdrawal amount (flat, all countries)',
      'Publishers without a bank account or Wise account',
    ],
  },
];

export const WISE_DIRECT_COLUMNS: FeeTableColumnType[] = [
  { title: 'Scenario' },
  { title: 'Wise fee', isFee: true },
];

export const WISE_DIRECT_ROWS: FeeTableRowType[] = [
  {
    cells: ['USD balance → your Wise account in USD (same currency)', 'Free'],
  },
  {
    cells: [
      'USD balance → your Wise account in another currency',
      'Conversion fee only: 0.22%-4.42% depending on the target currency',
    ],
  },
];

export const WISE_DIRECT_NOTES: string[] = [
  'Same-currency transfers between Wise accounts are free and usually instant.',
  'No SWIFT fees, no correspondent bank fees, no receiving fees apply.',
  'If you want another currency, you can also receive USD for free and convert inside your own Wise account whenever you choose.',
];

export const LOCAL_RAILS_COLUMNS: FeeTableColumnType[] = [
  { title: 'Destination' },
  { title: 'Receiving currency' },
  { title: 'Approx. conversion fee', isFee: true },
  { title: 'Cost per 1,000 USD' },
];

export const LOCAL_RAILS_ROWS: FeeTableRowType[] = [
  { cells: ['United Kingdom', 'GBP', '~0.42%-0.47%', '~4.20-4.70 USD'] },
  { cells: ['Eurozone (SEPA)', 'EUR', '~0.38%-0.42%', '~3.80-4.20 USD'] },
  { cells: ['Australia', 'AUD', '~0.38%', '~3.84 USD'] },
  { cells: ['Canada', 'CAD', '~0.40%-0.50%', '~4.00-5.00 USD'] },
  { cells: ['India', 'INR', '~0.56%', '~5.63 USD'] },
  {
    cells: [
      'Other / exotic currencies',
      'various',
      'up to ~2% (officially 0.22%-4.42% full range)',
      'varies',
    ],
  },
];

export const SWIFT_SPECIAL_COLUMNS: FeeTableColumnType[] = [
  { title: 'Destination' },
  { title: 'SWIFT fee per transfer', isFee: true },
];

export const SWIFT_SPECIAL_ROWS: FeeTableRowType[] = [
  { cells: ['Any country, no predicted correspondent fees', '7.41 USD'] },
  {
    cells: [
      'Countries with predicted correspondent fees (see the table below; Wise covers the correspondent fees so the recipient gets the full amount)',
      '7.94-35.00 USD',
    ],
  },
  {
    cells: [
      'Country with predicted correspondent fees but not listed in the table below',
      '21.71 USD',
    ],
  },
  { cells: ['Hong Kong (paid out locally, not SWIFT)', '8.44 USD'] },
  { cells: ['Singapore, DBS accounts only (paid out locally)', '9.25 USD'] },
  { cells: ['Ukraine, PrivatBank accounts only (paid out locally)', '1.13 USD + 2.11%'] },
];

export const EUR_SWIFT_COLUMNS: FeeTableColumnType[] = [
  { title: 'Scenario' },
  { title: 'SWIFT fee per transfer', isFee: true },
];

export const EUR_SWIFT_ROWS: FeeTableRowType[] = [
  {
    cells: [
      'Shared fees: sender pays the Wise SWIFT fee, recipient pays any correspondent/receiving bank fees (used when extra fees are unlikely or unpredictable)',
      '5.21 EUR',
    ],
  },
  {
    cells: [
      'Fixed fee: one fee covers everything, recipient gets the exact amount (used when Wise knows other banks will charge)',
      '25.22 EUR',
    ],
  },
  {
    cells: [
      'Ukraine, PrivatBank accounts (paid out locally, personal and business)',
      '1.31 EUR + 2.0%',
    ],
  },
];

export const CONVERSION_FEE_COLUMNS: FeeTableColumnType[] = [
  { title: 'Currency group' },
  { title: 'Typical conversion fee', isFee: true },
];

export const CONVERSION_FEE_ROWS: FeeTableRowType[] = [
  {
    cells: ['Major currencies (EUR, GBP, USD, CAD, AUD, NZD, CHF, SGD)', '~0.35%-0.65%'],
  },
  {
    cells: ['Popular remittance corridors (INR, PHP, MXN, PLN, ZAR, TRY…)', '~0.5%-1.2%'],
  },
  {
    cells: [
      'Exotic / restricted currencies (some African, Asian, LatAm currencies; BRL includes local IOF tax)',
      '~1%-4.42%',
    ],
  },
];

export const RECEIVING_FEE_COLUMNS: FeeTableColumnType[] = [
  { title: 'Receiving method' },
  { title: 'Fee to recipient', isFee: true },
];

export const RECEIVING_FEE_ROWS: FeeTableRowType[] = [
  { cells: ['Wise account, same-currency Wise transfer', 'Free'] },
  { cells: ['Local bank account, local rails', 'Free (no SWIFT/receiving fees)'] },
  { cells: ['US bank account, ACH', 'Free'] },
  { cells: ['Wise account, incoming USD wire', '6.11 USD'] },
  {
    cells: [
      'Wise account, incoming SWIFT payment',
      'Fixed fee by currency, e.g. USD 6.11, EUR 2.39, GBP 2.16, CAD 6.16, AUD 6.24, CHF 3.55, SGD 5.34, JPY 611, PLN 16.20',
    ],
  },
  {
    cells: [
      'Regular bank account, incoming SWIFT payment',
      'Set by your bank + possible correspondent bank deductions (commonly 10-25 USD/EUR) unless Wise prepaid them',
    ],
  },
];

export const RECOMMENDATIONS: RecommendationType[] = [
  {
    title: 'Have a Wise account?',
    text: 'Choose the direct Wise withdrawal in USD. It is free and usually instant. Convert inside Wise later if needed.',
  },
  {
    title: 'US bank account?',
    text: 'Bank transfer costs only 1.13 USD.',
  },
  {
    title: 'Bank account in local currency?',
    text: 'Local-rail payout at ~0.4%-1% total is far cheaper than SWIFT.',
  },
  {
    title: 'Need USD to a non-US bank account?',
    text: 'SWIFT is the most expensive route (7.41-35 USD + possible receiving fees). Consider opening a free Wise account instead.',
  },
  {
    title: 'PayPal',
    text: "The simplest option, but it costs a flat 2%, plus PayPal's own conversion spread for non-USD accounts.",
  },
];

export const SOURCE_LINKS: SourceLinkType[] = [
  {
    title: 'Wise: Fees for sending money',
    url: 'https://wise.com/help/articles/2522717/fees-for-sending-money',
  },
  {
    title: "Wise: How much does it cost to send money that's in a Wise account?",
    url: 'https://wise.com/help/articles/2949818/how-much-does-it-cost-to-send-money-thats-in-a-wise-account',
  },
  {
    title: 'Wise: Cost of sending USD to countries outside the US (per-country SWIFT table)',
    url: 'https://wise.com/help/articles/2946451/how-much-does-it-cost-to-send-usd-to-countries-outside-the-us',
  },
  {
    title: 'Wise: Sending EUR to countries outside of Europe',
    url: 'https://wise.com/help/articles/2968916/sending-eur-to-countries-outside-of-europe',
  },
  {
    title: 'Wise: Sending GBP to countries outside of the UK',
    url: 'https://wise.com/help/articles/2977936/sending-gbp-to-countries-outside-of-the-uk',
  },
  {
    title: 'Wise: Fees for receiving money by Swift',
    url: 'https://wise.com/help/articles/3EsxRDF4uNpQncdgH480os/fees-for-receiving-money-by-swift',
  },
  {
    title: 'Wise: Fees and limits for receiving money',
    url: 'https://wise.com/help/articles/2978013/what-are-the-fees-and-limits-for-receiving-money',
  },
  {
    title: 'Wise Inc. long-form fee disclosure (official US pricing)',
    url: 'https://wise.com/gateway/v1/us/disclosures/long-form',
  },
  {
    title: 'Wise pricing calculator',
    url: 'https://wise.com/us/pricing/send-money',
  },
];

export const SWIFT_COUNTRY_FEES: SwiftCountryFeeType[] = [
  { country: 'Albania', feeUsd: 15.93 },
  { country: 'Antigua and Barbuda', feeUsd: 24.07 },
  { country: 'Argentina', feeUsd: 22.25 },
  { country: 'Armenia', feeUsd: 18.87 },
  { country: 'Australia', feeUsd: 13.1 },
  { country: 'Austria', feeUsd: 26.0 },
  { country: 'Bahamas', feeUsd: 16.05 },
  { country: 'Belgium', feeUsd: 34.84 },
  { country: 'Bolivia', feeUsd: 14.61 },
  { country: 'Bosnia and Herzegovina', feeUsd: 24.32 },
  { country: 'Botswana', feeUsd: 15.35 },
  { country: 'Bulgaria', feeUsd: 35.0 },
  { country: 'Cambodia', feeUsd: 7.94 },
  { country: 'Canada', feeUsd: 12.38 },
  { country: 'Chile', feeUsd: 21.73 },
  { country: 'China', feeUsd: 14.34 },
  { country: 'Costa Rica', feeUsd: 13.97 },
  { country: 'Cyprus', feeUsd: 16.76 },
  { country: 'Czech Republic', feeUsd: 23.36 },
  { country: 'Denmark', feeUsd: 33.54 },
  { country: 'Dominican Republic', feeUsd: 9.54 },
  { country: 'Ecuador', feeUsd: 9.63 },
  { country: 'El Salvador', feeUsd: 10.18 },
  { country: 'Ethiopia', feeUsd: 17.41 },
  { country: 'France', feeUsd: 27.29 },
  { country: 'Georgia', feeUsd: 11.26 },
  { country: 'Ghana', feeUsd: 15.52 },
  { country: 'Guatemala', feeUsd: 9.98 },
  { country: 'Honduras', feeUsd: 10.02 },
  { country: 'Hungary', feeUsd: 22.8 },
  { country: 'India', feeUsd: 22.21 },
  { country: 'Indonesia', feeUsd: 35.0 },
  { country: 'Ireland', feeUsd: 24.86 },
  { country: 'Israel', feeUsd: 35.0 },
  { country: 'Italy', feeUsd: 28.43 },
  { country: 'Jamaica', feeUsd: 14.6 },
  { country: 'Kazakhstan', feeUsd: 12.22 },
  { country: 'Kenya', feeUsd: 19.53 },
  { country: 'Kyrgyzstan', feeUsd: 23.76 },
  { country: 'Lebanon', feeUsd: 15.81 },
  { country: 'Macedonia', feeUsd: 20.54 },
  { country: 'Malawi', feeUsd: 15.31 },
  { country: 'Malaysia', feeUsd: 19.74 },
  { country: 'Mauritius', feeUsd: 19.83 },
  { country: 'Mexico', feeUsd: 15.69 },
  { country: 'Mongolia', feeUsd: 34.89 },
  { country: 'Montenegro', feeUsd: 15.17 },
  { country: 'Morocco', feeUsd: 17.1 },
  { country: 'Nepal', feeUsd: 16.37 },
  { country: 'New Zealand', feeUsd: 22.61 },
  { country: 'Nicaragua', feeUsd: 8.39 },
  { country: 'Norway', feeUsd: 17.56 },
  { country: 'Panama', feeUsd: 13.39 },
  { country: 'Paraguay', feeUsd: 21.68 },
  { country: 'Peru', feeUsd: 35.0 },
  { country: 'Philippines', feeUsd: 10.4 },
  { country: 'Poland', feeUsd: 32.62 },
  { country: 'Portugal', feeUsd: 20.29 },
  { country: 'Romania', feeUsd: 30.35 },
  { country: 'Saint Lucia', feeUsd: 14.85 },
  { country: 'Serbia', feeUsd: 19.96 },
  { country: 'Singapore', feeUsd: 18.55 },
  { country: 'Slovenia', feeUsd: 18.77 },
  { country: 'South Africa', feeUsd: 18.57 },
  { country: 'South Korea', feeUsd: 35.0 },
  { country: 'Spain', feeUsd: 33.9 },
  { country: 'Sri Lanka', feeUsd: 18.74 },
  { country: 'Suriname', feeUsd: 15.24 },
  { country: 'Sweden', feeUsd: 21.07 },
  { country: 'Switzerland', feeUsd: 18.28 },
  { country: 'Taiwan', feeUsd: 19.29 },
  { country: 'Tanzania', feeUsd: 19.27 },
  { country: 'Thailand', feeUsd: 35.0 },
  { country: 'Timor-Leste', feeUsd: 15.0 },
  { country: 'Tunisia', feeUsd: 35.0 },
  { country: 'Turkey', feeUsd: 27.62 },
  { country: 'Turks and Caicos Islands', feeUsd: 15.42 },
  { country: 'Uganda', feeUsd: 14.67 },
  { country: 'United Arab Emirates', feeUsd: 24.93 },
  { country: 'United Kingdom', feeUsd: 22.26 },
  { country: 'Uruguay', feeUsd: 26.28 },
  { country: 'Uzbekistan', feeUsd: 18.07 },
  { country: 'Vietnam', feeUsd: 10.23 },
  { country: 'Zambia', feeUsd: 14.74 },
];
